import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

interface DosageRow {
  weightLb: number;
  weightKg: number;
  day1Dose: number;
  maintenanceDose: number;
}

@customElement('sulfadimethoxine-chart')
export class SulfadimethoxineChart extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state() private unit: 'lb' | 'kg' = 'lb';
  @state() private lookupWeight = '';
  @state() private lookupResult: { weight: string; day1: string; maintenance: string } | null = null;

  private dosageData: DosageRow[] = [
    { weightLb: 5, weightKg: 2.3, day1Dose: 114, maintenanceDose: 57 },
    { weightLb: 10, weightKg: 4.5, day1Dose: 227, maintenanceDose: 114 },
    { weightLb: 20, weightKg: 9.1, day1Dose: 455, maintenanceDose: 227 },
    { weightLb: 40, weightKg: 18.2, day1Dose: 910, maintenanceDose: 455 },
    { weightLb: 60, weightKg: 27.3, day1Dose: 1365, maintenanceDose: 682 },
    { weightLb: 80, weightKg: 36.4, day1Dose: 1820, maintenanceDose: 910 }
  ];

  private calculateCustomDose(weight: number, unit: 'lb' | 'kg') {
    const weightInKg = unit === 'lb' ? weight / 2.20462 : weight;
    const day1Dose = Math.round(weightInKg * 50); // 50 mg/kg
    const maintenanceDose = Math.round(weightInKg * 25); // 25 mg/kg
    return { day1Dose, maintenanceDose };
  }

  private handleUnitChange(newUnit: 'lb' | 'kg') {
    this.unit = newUnit;

    // If there's already a weight entered, recalculate for the new unit
    if (this.lookupWeight && parseFloat(this.lookupWeight) > 0) {
      const weight = parseFloat(this.lookupWeight);
      const doses = this.calculateCustomDose(weight, this.unit);
      this.lookupResult = {
        weight: `${weight} ${this.unit}`,
        day1: `${doses.day1Dose} mg`,
        maintenance: `${doses.maintenanceDose} mg`
      };
    }
    this.requestUpdate()
  }

  private handleLookupWeightChange(e: Event) {
    const target = e.target as HTMLInputElement;
    this.lookupWeight = target.value;

    if (this.lookupWeight && parseFloat(this.lookupWeight) > 0) {
      const weight = parseFloat(this.lookupWeight);
      const doses = this.calculateCustomDose(weight, this.unit);
      this.lookupResult = {
        weight: `${weight} ${this.unit}`,
        day1: `${doses.day1Dose} mg`,
        maintenance: `${doses.maintenanceDose} mg`
      };
    } else {
      this.lookupResult = null;
    }
    this.requestUpdate()
  }

  private printChart() {
    window.print();
  }

  private downloadCSV() {
    const headers = ['Weight', 'Day 1 Dose (mg)', 'Days 2+ Dose (mg)'];
    const rows = this.dosageData.map(row => [
      `${this.unit === 'lb' ? row.weightLb : row.weightKg} ${this.unit}`,
      row.day1Dose,
      row.maintenanceDose
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sulfadimethoxine-dosage-chart-${this.unit}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  render() {
    return html`
      <div class="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
        <h2 class="text-2xl font-bold text-gray-900 mb-6 text-center">
          Sulfadimethoxine (Albon®) Dosage Chart
        </h2>

        <div class="mb-6 flex flex-wrap gap-4 items-center justify-center">
          <div class="inline-flex rounded-lg border border-gray-300 p-1">
            <button
              class="px-3 py-1 text-sm font-medium rounded-md transition-colors ${this.unit === 'lb' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}"
              @click=${() => this.handleUnitChange('lb')}
            >
              Pounds (lb)
            </button>
            <button
              class="px-3 py-1 text-sm font-medium rounded-md transition-colors ${this.unit === 'kg' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}"
              @click=${() => this.handleUnitChange('kg')}
            >
              Kilograms (kg)
            </button>
          </div>
        </div>

        <div class="mb-6 p-4 bg-blue-50 rounded-lg">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Quick Lookup</h3>
          <div class="flex gap-3 items-center">
            <input
              type="number"
              class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1"
              placeholder="Enter dog weight"
              .value=${this.lookupWeight}
              @input=${this.handleLookupWeightChange}
              min="0"
              step="0.1"
            />
            <span class="text-sm text-gray-500">${this.unit}</span>
          </div>

          ${this.lookupResult ? html`
            <div class="mt-3 p-3 bg-white rounded border border-blue-200">
              <div class="font-semibold text-gray-900 mb-2">
                Dosage for ${this.lookupResult.weight}:
              </div>
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="font-medium">Day 1 (Loading):</span>
                  <div class="text-blue-600">${this.lookupResult.day1}</div>
                </div>
                <div>
                  <span class="font-medium">Days 2+ (Maintenance):</span>
                  <div class="text-blue-600">${this.lookupResult.maintenance}</div>
                </div>
              </div>
            </div>
          ` : ''}
        </div>

        <div class="overflow-x-auto">
          <table class="w-full border-collapse bg-white rounded-lg overflow-hidden shadow">
            <thead class="bg-gray-50 text-gray-700 font-semibold text-sm">
              <tr>
                <th class="px-4 py-3 text-left border-b border-gray-200">Dog Weight</th>
                <th class="px-4 py-3 text-left border-b border-gray-200">Day 1 Dose</th>
                <th class="px-4 py-3 text-left border-b border-gray-200">Days 2–14 Dose</th>
              </tr>
            </thead>
            <tbody>
              ${this.dosageData.map(row => html`
                <tr class="border-b border-gray-100 hover:bg-gray-50">
                  <td class="px-4 py-3 text-sm font-medium text-gray-900">
                    ${this.unit === 'lb' ? row.weightLb : row.weightKg} ${this.unit}
                    ${this.unit === 'lb' ? html`<span class="text-gray-500 text-xs block">(${row.weightKg} kg)</span>` :
                      html`<span class="text-gray-500 text-xs block">(${row.weightLb} lb)</span>`}
                  </td>
                  <td class="px-4 py-3 text-sm font-medium text-blue-600">
                    ${row.day1Dose} mg
                  </td>
                  <td class="px-4 py-3 text-sm font-medium text-green-600">
                    ${row.maintenanceDose} mg
                  </td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>

        <div class="mt-6 flex flex-wrap gap-3 justify-center print:hidden">
          <button class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors" @click=${this.printChart}>
            🖨️ Print Chart
          </button>
          <button class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors" @click=${this.downloadCSV}>
            📥 Download CSV
          </button>
        </div>

        <div class="text-xs text-gray-600 mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <strong>Important:</strong>
          <span>
            This chart shows standard dosages based on 50 mg/kg loading dose (Day 1)
            and 25 mg/kg maintenance dose (Days 2+). Always consult your veterinarian for proper diagnosis,
            treatment duration, and monitoring. Dosages are calculated for informational purposes only.
          </span>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sulfadimethoxine-chart': SulfadimethoxineChart;
  }
}
